import { Collaboration, ConversationWork } from './Collaboration.js';
import { storageLevel } from '../shared/storage.js';
import { t, setLocale, useLocale } from './i18n.js';
import { Calendar, type CalendarSeed } from './Calendar.js';
import { AgendaPanel } from './AgendaPanel.js';
import { Contacts } from './Contacts.js';
import { CalendarDays, UserRound, UsersRound, CalendarOff, Clock } from 'lucide-react';
import { Scheduled } from './Scheduled.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Mail,
  Inbox,
  Send,
  FilePenLine,
  Archive,
  ShieldAlert,
  Trash2,
  Folder,
  Star,
  Plus,
  Search,
  SlidersHorizontal,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  MoreHorizontal,
  Paperclip,
  Reply,
  ReplyAll,
  Forward,
  Download,
  Settings as SettingsIcon,
  ShieldCheck,
  Grid2X2,
  HelpCircle,
  Bell,
  PanelLeftClose,
  Menu,
  X,
  Check,
  ArrowLeft,
  MailOpen,
  CheckCheck,
  WifiOff,
  FolderInput,
  ExternalLink,
  FileText,
  LoaderCircle,
  LogOut,
  Undo2,
} from 'lucide-react';
import type {
  Bootstrap,
  Folder as MailFolder,
  Message,
  MessageSummary,
  Submission,
} from '../shared/types.js';
import { api, send, setCsrf, setMailboxId, mailboxUrl, onUnauthorized } from './api.js';
import {
  Logo,
  Avatar,
  IconButton,
  Modal,
  Notice,
  Loading,
  formatBytes,
  formatStorageBytes,
  displayDate,
} from './components.js';
import { Login } from './Login.js';
import { Composer, type ComposeSeed } from './Composer.js';
import { Admin, Platform } from './Admin.js';
import { Settings } from './Settings.js';
import { sessionStore, takeEntryView, takeReturnTab } from './entry.js';
const folderIcons: Record<string, typeof Inbox> = {
  inbox: Inbox,
  drafts: FilePenLine,
  sent: Send,
  archive: Archive,
  spam: ShieldAlert,
  trash: Trash2,
  custom: Folder,
};
const labels = (): Record<string, string> => ({
  inbox: t('Boîte de réception'),
  drafts: t('Brouillons'),
  sent: t('Messages envoyés'),
  archive: t('Archives'),
  spam: t('Indésirables'),
  trash: t('Corbeille'),
  starred: t('Favoris'),
});
export function App() {
  // Re-render the whole shell when the language changes; the preference follows the account.
  useLocale();
  const [auth, setAuth] = useState<any>(),
    [bootstrap, setBootstrap] = useState<Bootstrap>(),
    [settingsTab, setSettingsTab] = useState('profile'),
    [calendarSeed, setCalendarSeed] = useState<CalendarSeed>(),
    [agendaOpen, setAgendaOpen] = useState(() => {
      try {
        return localStorage.getItem('vename.agenda-panel') === '1';
      } catch {
        return false;
      }
    }),
    [initializing, setInitializing] = useState(true),
    [view, setView] = useState('mail'),
    [folder, setFolder] = useState('inbox'),
    [messages, setMessages] = useState<MessageSummary[]>([]),
    [selected, setSelected] = useState<Message>(),
    [selectedIds, setSelectedIds] = useState<Set<string>>(new Set()),
    [loading, setLoading] = useState(false),
    [reading, setReading] = useState(false),
    [q, setQ] = useState(''),
    [debounced, setDebounced] = useState(''),
    [filter, setFilter] = useState('all'),
    [filtersOpen, setFiltersOpen] = useState(false),
    [sender, setSender] = useState(''),
    [after, setAfter] = useState(''),
    [before, setBefore] = useState(''),
    [next, setNext] = useState<string | null>(null),
    [compose, setCompose] = useState<ComposeSeed>(),
    [toast, setToast] = useState(''),
    [error, setError] = useState(''),
    [modal, setModal] = useState(''),
    [newFolder, setNewFolder] = useState(''),
    [mobileNav, setMobileNav] = useState(false),
    [network, setNetwork] = useState(navigator.onLine),
    [submission, setSubmission] = useState<Submission>(),
    [messageMenu, setMessageMenu] = useState(false),
    [textOnly, setTextOnly] = useState(false),
    [folderEdit, setFolderEdit] = useState<MailFolder>(),
    [busy, setBusy] = useState(false);
  const bootstrapRequest = useRef(0);
  const listRequest = useRef(0),
    readRequest = useRef(0),
    search = useRef<HTMLInputElement>(null),
    toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const notify = useCallback((s: string) => {
    setToast(s);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 4500);
  }, []);
  const switchMailbox = async (id: string) => {
    if (compose) {
      notify(t('Fermez ou enregistrez le message ouvert avant de changer de boîte.'));
      return;
    }
    ++listRequest.current;
    ++readRequest.current;
    setMessages([]);
    setSelected(undefined);
    setSelectedIds(new Set());
    setSubmission(undefined);
    setFolder('inbox');
    setQ('');
    setDebounced('');
    setNext(null);
    const previous = bootstrap?.mailbox?.id ?? '';
    setMailboxId(id);
    try {
      await loadBootstrap();
      setView('mail');
    } catch (e: any) {
      setMailboxId(previous);
      setError(e.message);
    }
  };
  useEffect(() => {
    if (bootstrap?.user.locale) setLocale(bootstrap.user.locale);
  }, [bootstrap?.user.locale]);
  // Opened from agenda.vename.com or contacts.vename.com: show that application once signed in.
  useEffect(() => {
    if (!bootstrap) return;
    const entry = takeEntryView(sessionStore());
    if (entry && bootstrap.mailbox) setView(entry);
    // Back from the sign-in that confirms a sensitive action: reopen its settings tab.
    const returnTab = takeReturnTab(sessionStore());
    if (returnTab) {
      setSettingsTab(returnTab);
      setView('settings');
    }
  }, [bootstrap]);
  const loadBootstrap = useCallback(async () => {
    const request = ++bootstrapRequest.current;
    const b = await api<Bootstrap>('/bootstrap');
    if (request === bootstrapRequest.current) {
      setCsrf(b.csrfToken);
      setBootstrap(b);
    }
    return b;
  }, []);
  const authenticate = async () => {
    setMailboxId('');
    try {
      const s = await api('/auth/session');
      setCsrf(s.csrfToken);
      setAuth(s);
      if (!s.mfaRequired) await loadBootstrap();
    } catch (e: any) {
      if (e.status !== 401) setError(e.message);
    } finally {
      setInitializing(false);
    }
  };
  useEffect(() => {
    void authenticate();
    const online = () => setNetwork(navigator.onLine);
    window.addEventListener('online', online);
    window.addEventListener('offline', online);
    return () => {
      window.removeEventListener('online', online);
      window.removeEventListener('offline', online);
    };
  }, []);
  useEffect(() => {
    const debounceTimer = setTimeout(() => setDebounced(q), 300);
    return () => clearTimeout(debounceTimer);
  }, [q]);
  const currentFolder = bootstrap?.folders.find((f) => f.kind === folder || f.id === folder);
  const dateRangeError =
    after && before && after > before ? t('La date de fin doit suivre la date de début.') : '';
  const pagesLoaded = useRef(1);
  const listBusy = useRef(false);
  const loadMessages = useCallback(
    async (append = false) => {
      if (
        !bootstrap?.mailbox ||
        (bootstrap.mailbox.kind === 'shared' && !bootstrap.mailbox.rights?.includes('read'))
      )
        return;
      const seq = ++listRequest.current;
      if (after && before && after > before) {
        setMessages([]);
        setNext(null);
        setError('');
        setLoading(false);
        listBusy.current = false;
        return;
      }
      setLoading(true);
      listBusy.current = true;
      setError('');
      try {
        const p = new URLSearchParams();
        if (debounced) p.set('q', debounced);
        else if (currentFolder) p.set('folder', currentFolder.id);
        if (folder === 'starred' || filter === 'starred') p.set('starred', 'true');
        if (filter === 'unread') p.set('unread', 'true');
        if (sender) p.set('sender', sender);
        if (after) p.set('after', after);
        if (before) p.set('before', before);
        if (append && next) p.set('cursor', next);
        const r = await api(`/messages?${p}`);
        if (seq !== listRequest.current) return;
        setMessages((old) =>
          append
            ? [...old, ...r.items.filter((m: MessageSummary) => !old.some((o) => o.id === m.id))]
            : r.items,
        );
        setNext(r.nextCursor);
        pagesLoaded.current = append ? pagesLoaded.current + 1 : 1;
        if (r.searchLimited)
          notify(t('Recherche locale limitée aux 20 000 messages les plus récents.'));
      } catch (e: any) {
        if (seq === listRequest.current) setError(e.message);
      } finally {
        if (seq === listRequest.current) {
          setLoading(false);
          listBusy.current = false;
        }
      }
    },
    [
      bootstrap?.mailbox?.id,
      currentFolder?.id,
      debounced,
      folder,
      filter,
      sender,
      after,
      before,
      next,
    ],
  );
  useEffect(() => {
    setSelected(undefined);
    ++readRequest.current;
    setReading(false);
    setSelectedIds(new Set());
    void loadMessages();
  }, [bootstrap?.mailbox?.id, currentFolder?.id, debounced, folder, filter, sender, after, before]);
  useEffect(() => {
    if (!bootstrap?.mailbox || view !== 'mail') return;
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        // Refreshing the list returns to its first page: not while further pages are shown or
        // one is loading, which would lose the reader's place. Counters still refresh.
        if (pagesLoaded.current <= 1 && !listBusy.current) void loadMessages();
        void loadBootstrap().catch(() => {});
      }
    }, 30000);
    return () => clearInterval(poll);
  }, [bootstrap?.mailbox?.id, view, loadMessages]);
  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMessageMenu(false);
        setMobileNav(false);
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        search.current?.focus();
      }
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);
  const refresh = useCallback(async () => {
    await Promise.all([loadMessages(), loadBootstrap()]);
  }, [loadMessages, loadBootstrap]);
  useEffect(() => {
    if (!submission || !['pending', 'submitting'].includes(submission.status)) return;
    let canceled = false;
    const timer = setInterval(() => {
      api<Submission>(`/submissions/${submission.id}`)
        .then((r) => {
          if (canceled) return;
          setSubmission(r);
          if (!['pending', 'submitting'].includes(r.status)) {
            notify(
              r.status === 'delivered_local'
                ? t('Message remis dans les boîtes locales.')
                : r.status === 'accepted'
                  ? t('Envoi pris en charge par le serveur mail.')
                  : t('Consultez l’état de votre envoi.'),
            );
            void refresh();
          }
        })
        .catch(() => {});
    }, 1000);
    return () => {
      canceled = true;
      clearInterval(timer);
    };
  }, [submission?.id, submission?.status, refresh, notify]);
  const action = async (action: string, ids: string[], folderId?: string) => {
    if (!ids.length || busy) return;
    setBusy(true);
    try {
      if (
        action === 'delete' &&
        !window.confirm(
          t('Supprimer définitivement {n} message(s) ? Cette action est irréversible.', {
            n: ids.length,
          }),
        )
      )
        return;
      await send('/messages/actions', { ids, action, folderId, confirm: action === 'delete' });
      if (selected && ids.includes(selected.id)) {
        if (['read', 'unread', 'star', 'unstar'].includes(action))
          setSelected({
            ...selected,
            ...(action === 'read'
              ? { read: true }
              : action === 'unread'
                ? { read: false }
                : action === 'star'
                  ? { starred: true }
                  : { starred: false }),
          });
        else setSelected(undefined);
      }
      setSelectedIds(new Set());
      await refresh();
      if (!['read', 'star', 'unstar'].includes(action))
        notify(
          action === 'trash'
            ? t(
                ids.length > 1
                  ? '{n} messages déplacés dans la corbeille.'
                  : 'Message déplacé dans la corbeille.',
                { n: ids.length },
              )
            : action === 'archive'
              ? t(ids.length > 1 ? '{n} messages archivés.' : 'Message archivé.', { n: ids.length })
              : action === 'restore'
                ? t(
                    ids.length > 1
                      ? '{n} messages restaurés dans la réception.'
                      : 'Message restauré dans la réception.',
                    { n: ids.length },
                  )
                : action === 'delete'
                  ? t(
                      ids.length > 1
                        ? '{n} messages supprimés définitivement.'
                        : 'Message supprimé définitivement.',
                      { n: ids.length },
                    )
                  : t('Messages mis à jour.'),
        );
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const openMessage = async (m: Pick<MessageSummary, 'id' | 'read'>) => {
    const seq = ++readRequest.current;
    setReading(true);
    setError('');
    setMessageMenu(false);
    try {
      const detail = await api<Message>(`/messages/${m.id}`);
      if (seq !== readRequest.current) return;
      if (currentFolder?.kind === 'drafts') {
        if (compose) {
          notify(t('Terminez le message ouvert avant de modifier ce brouillon.'));
          return;
        }
        setCompose({ mode: 'draft', message: detail });
        return;
      }
      setSelected(detail);
      setTextOnly(false);
      if (!m.read) {
        await send('/messages/actions', { ids: [m.id], action: 'read' });
        setMessages((old) => old.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
        setSelected((s) => (s?.id === m.id ? { ...s, read: true } : s));
        void loadBootstrap().catch(() => {});
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      if (seq === readRequest.current) setReading(false);
    }
  };
  const startCompose = (seed: ComposeSeed = { mode: 'new' }) => {
    if (compose) {
      notify(t('Un message est déjà ouvert. Vous pouvez le fermer en conservant son brouillon.'));
      return false;
    }
    setCompose(seed);
    setMobileNav(false);
    return true;
  };
  const navigate = (id: string) => {
    setView('mail');
    setFolder(id);
    setQ('');
    setFilter('all');
    setMobileNav(false);
    setSelected(undefined);
  };
  const toggleAgenda = (open = !agendaOpen) => {
    setAgendaOpen(open);
    try {
      localStorage.setItem('vename.agenda-panel', open ? '1' : '0');
    } catch {
      // Remembering the panel is a convenience only.
    }
  };
  const openCalendarAt = (date: string, create = false) => {
    setCalendarSeed({ date, create, key: Date.now() });
    setView('calendar');
    setSelected(undefined);
  };
  // Back to the sign-in screen with nothing of the previous session: another person may sign in.
  const resetSession = () => {
    setBootstrap(undefined);
    setAuth(undefined);
    setSelected(undefined);
    setMessages([]);
    setCsrf('');
    setMailboxId('');
    setView('mail');
    setFolder('inbox');
    setQ('');
    setFilter('all');
    setModal('');
    setSettingsTab('profile');
  };
  const logout = async () => {
    if (compose) {
      notify(t('Fermez votre brouillon pour l’enregistrer avant de vous déconnecter.'));
      return;
    }
    try {
      await send('/auth/logout', {});
      resetSession();
      setError('');
    } catch (e: any) {
      setError(e.message);
    }
  };
  // An expired or revoked session returns to sign-in, except while a message is being written:
  // its text would be lost, so the person is asked to copy it first.
  const composeOpen = useRef(false);
  composeOpen.current = Boolean(compose);
  useEffect(() => {
    onUnauthorized(() => {
      if (composeOpen.current) {
        setError(t('Votre session a expiré. Copiez votre texte, puis reconnectez-vous.'));
        return;
      }
      resetSession();
      setError(t('Votre session a expiré. Reconnectez-vous.'));
    });
    return () => onUnauthorized(undefined);
  }, []);
  if (initializing)
    return (
      <div className="splash">
        <Logo />
        <Loading label={t('Ouverture de votre espace…')} />
      </div>
    );
  if (!bootstrap)
    return (
      <>
        {error && (
          <div className="login-global-error">
            <Notice type="error">{error}</Notice>
          </div>
        )}
        <Login initial={auth} onSuccess={() => void authenticate()} />
      </>
    );
  if (!bootstrap.mailbox)
    return (
      <main className="workspace-page">
        <header className="page-heading">
          <h1>{t('Administration Vename Mail')}</h1>
          <button className="secondary" onClick={() => void logout()}>
            {t('Se déconnecter')}
          </button>
        </header>
        <p>{t('Compte d’administration sans boîte mail. Aucune unité de messagerie consommée.')}</p>
        {bootstrap.user.role === 'operator' ? (
          <Platform notify={notify} />
        ) : (
          <Admin
            bootstrap={bootstrap}
            notify={notify}
            onQuotaChange={() => void loadBootstrap().catch(() => {})}
          />
        )}
      </main>
    );
  const unread = bootstrap.folders.find((f) => f.kind === 'inbox')?.unread ?? 0,
    title = debounced
      ? t('Résultats de recherche')
      : (labels()[folder] ?? currentFolder?.name ?? t('Messages'));
  const selectedIndex = messages.findIndex((m) => m.id === selected?.id);
  const allSelected = messages.length > 0 && messages.every((m) => selectedIds.has(m.id));
  return (
    <div className="app-shell">
      <a href="#main-content" className="skip-link">
        {t('Aller au contenu')}
      </a>
      <aside className="app-rail" aria-label={t('Espaces Vename Mail')}>
        <button
          className="rail-brand"
          aria-label={t('Messagerie Vename Mail')}
          onClick={() => navigate('inbox')}
        >
          <Logo compact />
        </button>
        <button
          className={`rail-item ${view === 'mail' ? 'active' : ''}`}
          aria-label={t('Messagerie')}
          title={t('Messagerie')}
          onClick={() => navigate('inbox')}
        >
          <Mail size={22} />
        </button>
        {bootstrap.mailbox && (
          <button
            className={`rail-item ${view === 'calendar' ? 'active' : ''}`}
            aria-label={t('Agenda')}
            title={t('Agenda')}
            onClick={() => setView('calendar')}
          >
            <CalendarDays size={22} />
          </button>
        )}
        {bootstrap.mailbox && (
          <button
            className={`rail-item ${view === 'contacts' ? 'active' : ''}`}
            aria-label={t('Carnet d’adresses')}
            title={t('Carnet d’adresses')}
            onClick={() => setView('contacts')}
          >
            <UserRound size={22} />
          </button>
        )}
        <button
          className={`rail-item ${view === 'team' ? 'active' : ''}`}
          aria-label={t('Espace d’équipe')}
          title={t('Espace d’équipe')}
          onClick={() => setView('team')}
        >
          <UsersRound size={22} />
        </button>
        {['owner', 'admin'].includes(bootstrap.user.role) && (
          <button
            className={`rail-item ${view === 'admin' ? 'active' : ''}`}
            aria-label={t('Administration')}
            title={t('Administration')}
            onClick={() => setView('admin')}
          >
            <Grid2X2 size={21} />
          </button>
        )}
        {bootstrap.user.role === 'operator' && (
          <button
            className={`rail-item ${view === 'platform' ? 'active' : ''}`}
            aria-label={t('Console Vename')}
            title={t('Console Vename')}
            onClick={() => setView('platform')}
          >
            <ShieldCheck size={22} />
          </button>
        )}
        <span className="spacer" />
        <button
          className={`rail-item ${view === 'settings' ? 'active' : ''}`}
          aria-label={t('Paramètres')}
          title={t('Paramètres')}
          onClick={() => setView('settings')}
        >
          <SettingsIcon size={21} />
        </button>
        <button
          className="rail-profile"
          aria-label={t('Mon profil')}
          onClick={() => setView('settings')}
        >
          <Avatar name={bootstrap.user.name} size="small" />
        </button>
        <button
          className="rail-item rail-logout"
          aria-label={t('Se déconnecter')}
          title={t('Se déconnecter')}
          onClick={() => void logout()}
        >
          <LogOut size={21} />
        </button>
      </aside>
      {!['calendar', 'contacts', 'team'].includes(view) && (
        <aside
          className={`sidebar ${mobileNav ? 'open' : ''}`}
          aria-label={t('Dossiers de messagerie')}
        >
          <div className="sidebar-brand">
            <Logo />
            <span className="badge neutral">{t('BÊTA')}</span>
            <IconButton
              className="mobile-only"
              label={t('Fermer la navigation')}
              onClick={() => setMobileNav(false)}
            >
              <X size={19} />
            </IconButton>
          </div>
          <button
            className="organization-button"
            onClick={() =>
              setView(['owner', 'admin'].includes(bootstrap.user.role) ? 'admin' : 'settings')
            }
          >
            <span className="organization-mark">
              {bootstrap.organization.name
                .split(' ')
                .map((s) => s[0])
                .slice(0, 2)
                .join('')}
            </span>
            <span>
              <strong>{bootstrap.organization.name}</strong>
              <small>{t('Espace professionnel')}</small>
            </span>
            <ChevronDown size={16} />
          </button>
          {(bootstrap.accessibleMailboxes?.length ?? 0) > 1 && (
            <label className="mailbox-switch">
              {t('Boîte ouverte')}
              <select
                aria-label={t('Boîte ouverte')}
                value={bootstrap.mailbox.id}
                onChange={(e) => void switchMailbox(e.target.value)}
              >
                {bootstrap.accessibleMailboxes?.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.address}
                    {m.kind === 'shared' ? ' · ' + t('partagée') : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button
            className="compose-button"
            disabled={
              bootstrap.mailbox.kind === 'shared' &&
              !bootstrap.mailbox.rights?.some((r) => ['sendAs', 'sendOnBehalf'].includes(r))
            }
            onClick={() => startCompose()}
          >
            <Plus size={21} />
            {t('Nouveau message')}
          </button>
          <nav className="folders">
            <span className="nav-caption">{t('MESSAGERIE')}</span>
            {['inbox', 'starred', 'drafts', 'sent', 'archive', 'spam', 'trash'].flatMap((kind) => {
              const f = bootstrap.folders.find((f) => f.kind === kind),
                Icon = kind === 'starred' ? Star : folderIcons[kind],
                count = kind === 'drafts' ? f?.total : f?.unread;
              const button = (
                <button
                  key={kind}
                  className={`folder-link ${view === 'mail' && folder === kind ? 'active' : ''}`}
                  onClick={() => navigate(kind)}
                >
                  <Icon size={19} />
                  <span>{kind === 'sent' ? t('Envoyés') : labels()[kind]}</span>
                  {!!count && <span className="folder-count">{count}</span>}
                </button>
              );
              if (kind !== 'drafts') return [button];
              return [
                button,
                <button
                  key="scheduled"
                  className={`folder-link ${view === 'scheduled' ? 'active' : ''}`}
                  onClick={() => {
                    setView('scheduled');
                    setSelected(undefined);
                    setMobileNav(false);
                  }}
                >
                  <Clock size={19} />
                  <span>{t('Programmés')}</span>
                  {!!bootstrap.scheduledCount && (
                    <span className="folder-count">{bootstrap.scheduledCount}</span>
                  )}
                </button>,
              ];
            })}
            <div className="nav-caption custom-caption">
              {t('MES DOSSIERS')}
              <IconButton
                label={t('Créer un dossier')}
                onClick={() => {
                  setNewFolder('');
                  setModal('folder');
                }}
              >
                <Plus size={16} />
              </IconButton>
            </div>
            {bootstrap.folders
              .filter((f) => f.kind === 'custom')
              .map((f) => (
                <div className="custom-folder" key={f.id}>
                  <button
                    className={`folder-link ${folder === f.id ? 'active' : ''}`}
                    aria-label={
                      f.unread > 0
                        ? `${f.name}, ${t(f.unread > 1 ? '{n} non lus' : '{n} non lu', { n: f.unread })}`
                        : f.name
                    }
                    onClick={() => navigate(f.id)}
                  >
                    <Folder size={18} />
                    <span>{f.name}</span>
                    {f.unread > 0 && (
                      <span className="folder-count" aria-hidden="true">
                        {f.unread}
                      </span>
                    )}
                  </button>
                  <IconButton
                    label={t('Gérer le dossier {name}', { name: f.name })}
                    onClick={() => {
                      setFolderEdit(f);
                      setNewFolder(f.name);
                      setModal('folderEdit');
                    }}
                  >
                    <MoreHorizontal size={15} />
                  </IconButton>
                </div>
              ))}
          </nav>
          <div className="sidebar-bottom">
            <div className="storage-heading">
              <span>
                <CloudIcon />
                {t('Stockage utilisé')}
              </span>
              <strong>
                {((bootstrap.mailbox.usedBytes / bootstrap.mailbox.quotaBytes) * 100).toFixed(0)} %
              </strong>
            </div>
            <progress
              aria-label={t('Stockage utilisé')}
              className={`storage-level-${storageLevel(bootstrap.mailbox.usedBytes, bootstrap.mailbox.quotaBytes)}`}
              max={bootstrap.mailbox.quotaBytes}
              value={bootstrap.mailbox.usedBytes}
            />
            <small>
              {t('{used} sur {total}', {
                used: formatStorageBytes(bootstrap.mailbox.usedBytes, 1),
                total: formatStorageBytes(bootstrap.mailbox.quotaBytes, 1),
              })}
            </small>
            <div className="local-status">
              <span className="status-dot" />
              {bootstrap.mode === 'local'
                ? t('Espace local · Données fictives')
                : t('Messagerie professionnelle')}
            </div>
            <button className="sidebar-help" onClick={() => setModal('help')}>
              <HelpCircle size={17} />
              <span>{t('Un coup de main ?')}</span>
              <ArrowUpRightIcon />
            </button>
          </div>
        </aside>
      )}
      {!['calendar', 'contacts', 'team'].includes(view) && mobileNav && (
        <div className="nav-backdrop" onClick={() => setMobileNav(false)} />
      )}
      <div className="app-main">
        {!['calendar', 'contacts', 'team'].includes(view) && (
          <header className="topbar">
            <IconButton
              className="mobile-only"
              label={t('Ouvrir la navigation')}
              onClick={() => setMobileNav(true)}
            >
              <Menu size={22} />
            </IconButton>
            <div className="search-box">
              <Search size={20} />
              <input
                ref={search}
                aria-label={t('Rechercher dans mes messages')}
                placeholder={t('Rechercher dans vos messages')}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setView('mail');
                }}
              />
              <kbd>⌘ K</kbd>
              {q && (
                <IconButton label={t('Effacer la recherche')} onClick={() => setQ('')}>
                  <X size={16} />
                </IconButton>
              )}
              <IconButton
                label={t('Filtres de recherche')}
                active={filtersOpen}
                onClick={() => {
                  setView('mail');
                  setFiltersOpen(!filtersOpen);
                }}
              >
                <SlidersHorizontal size={18} />
              </IconButton>
            </div>
            <div className="topbar-right">
              <span className="network-status">
                <span className={`status-dot ${network ? '' : 'offline'}`} />
                {network ? t('Connecté') : t('Hors connexion')}
              </span>
              <IconButton
                label={t('Volet Agenda')}
                active={agendaOpen}
                onClick={() => toggleAgenda()}
              >
                <CalendarDays size={20} />
              </IconButton>
              <IconButton label={t('Centre d’aide')} onClick={() => setModal('help')}>
                <HelpCircle size={20} />
              </IconButton>
              <IconButton label={t('Notifications')} onClick={() => setModal('notifications')}>
                <Bell size={20} />
                {submission?.status === 'failed' && <i className="notification-dot" />}
              </IconButton>
              <span className="topbar-divider" />
              <button
                className="profile-button"
                aria-label={t('Ouvrir mon profil')}
                onClick={() => {
                  setSettingsTab('profile');
                  setView('settings');
                }}
              >
                <Avatar name={bootstrap.user.name} />
                <ChevronDown size={15} />
              </button>
            </div>
          </header>
        )}
        {!network && (
          <div className="offline-bar" role="status">
            <WifiOff size={16} />
            {t('Connexion interrompue. Vos modifications restent dans cet onglet.')}
          </div>
        )}
        <div id="main-content" className="content-area" tabIndex={-1}>
          {view === 'team' ? (
            <Collaboration role={bootstrap.user.role} />
          ) : view === 'calendar' ? (
            <Calendar seed={calendarSeed} />
          ) : view === 'contacts' ? (
            <Contacts
              onCompose={(email) => {
                if (startCompose({ mode: 'new', to: [email] })) setView('mail');
              }}
            />
          ) : view === 'admin' ? (
            <Admin
              bootstrap={bootstrap}
              notify={notify}
              onQuotaChange={() => void loadBootstrap().catch(() => {})}
            />
          ) : view === 'platform' ? (
            <Platform notify={notify} />
          ) : view === 'scheduled' ? (
            <Scheduled
              composeOpen={Boolean(compose)}
              notify={notify}
              onChange={() => void loadBootstrap()}
              onEdit={async (draftId) => {
                const detail = await api<Message>(`/messages/${draftId}`);
                setView('mail');
                setCompose({ mode: 'draft', message: detail });
              }}
            />
          ) : view === 'settings' ? (
            <Settings
              bootstrap={bootstrap}
              onUpdate={() => void loadBootstrap()}
              notify={notify}
              onLogout={() => void logout()}
              initialTab={settingsTab}
            />
          ) : (
            <main className={`mail-workspace ${selected ? 'has-selection' : ''}`}>
              {bootstrap.oidcEnabled && !bootstrap.user.mfaEnabled && (
                <MfaReminder
                  onOpen={() => {
                    setSettingsTab('security');
                    setView('settings');
                  }}
                />
              )}
              {bootstrap.autoReply?.active && (
                <div className="autoreply-bar" role="status">
                  <CalendarOff size={16} />
                  <span>
                    {t('Votre réponse d’absence est active')}
                    {bootstrap.autoReply.endsAt
                      ? ` ${t('jusqu’au')} ${displayDate(bootstrap.autoReply.endsAt, true)}`
                      : ''}
                    .
                  </span>
                  <button
                    className="text-button"
                    onClick={() => {
                      setSettingsTab('absence');
                      setView('settings');
                    }}
                  >
                    {t('Modifier')}
                  </button>
                  <button
                    className="text-button"
                    onClick={() =>
                      void api<any>('/autoreply')
                        .then((r) => send('/autoreply', { ...r, enabled: false }, 'PUT'))
                        .then(() => loadBootstrap())
                        .then(() => notify(t('Réponse d’absence désactivée.')))
                        .catch((e) => setError(e.message))
                    }
                  >
                    {t('Désactiver')}
                  </button>
                </div>
              )}
              <div className="mail-heading">
                <div>
                  <div className="heading-line">
                    <h1>{title}</h1>
                    {folder === 'inbox' && !debounced && (
                      <span className="unread-pill">
                        {t(unread > 1 ? '{n} non lus' : '{n} non lu', { n: unread })}
                      </span>
                    )}
                  </div>
                  <p>
                    {debounced
                      ? t('Recherche « {q} » dans votre boîte', { q: debounced })
                      : folder === 'spam' || folder === 'trash'
                        ? t('Les messages sont conservés pendant 30 jours avant leur suppression.')
                        : t('Un peu de clarté dans votre journée.')}
                  </p>
                </div>
                <div className="heading-actions">
                  <IconButton
                    label={t('Actualiser les messages')}
                    onClick={() => void refresh().catch((e) => setError(e.message))}
                  >
                    <RefreshCw size={18} className={loading ? 'spin' : ''} />
                  </IconButton>
                  <IconButton
                    label={t('Informations sur ce dossier')}
                    onClick={() => setModal('folderInfo')}
                  >
                    <MoreHorizontal size={21} />
                  </IconButton>
                </div>
              </div>
              {filtersOpen && (
                <div className="search-filters">
                  <label>
                    {t('Expéditeur')}
                    <input
                      value={sender}
                      onChange={(e) => setSender(e.target.value)}
                      placeholder="adresse@entreprise.com"
                    />
                  </label>
                  <label>
                    {t('À partir du')}
                    <input
                      type="date"
                      value={after}
                      onChange={(e) => setAfter(e.target.value)}
                      aria-invalid={Boolean(dateRangeError)}
                      aria-describedby={dateRangeError ? 'mail-date-range-error' : undefined}
                    />
                  </label>
                  <label>
                    {t('Jusqu’au')}
                    <input
                      type="date"
                      value={before}
                      onChange={(e) => setBefore(e.target.value)}
                      aria-invalid={Boolean(dateRangeError)}
                      aria-describedby={dateRangeError ? 'mail-date-range-error' : undefined}
                    />
                  </label>
                  {dateRangeError && (
                    <p id="mail-date-range-error" className="search-filter-error" role="alert">
                      {dateRangeError}
                    </p>
                  )}
                  <button
                    className="text-button"
                    onClick={() => {
                      setSender('');
                      setAfter('');
                      setBefore('');
                    }}
                  >
                    {t('Réinitialiser')}
                  </button>
                </div>
              )}
              {error && (
                <Notice type="error">
                  {error}
                  <button
                    className="text-button"
                    onClick={() => void refresh().catch((e) => setError(e.message))}
                  >
                    {t('Réessayer')}
                  </button>
                </Notice>
              )}
              <div className="mail-surface">
                <section className="message-list-pane" aria-label={t('Liste des messages')}>
                  <div className="list-tabs">
                    <button
                      className={filter === 'all' ? 'active' : ''}
                      onClick={() => setFilter('all')}
                    >
                      {t('Tous les messages')}
                    </button>
                    <button
                      className={filter === 'unread' ? 'active' : ''}
                      onClick={() => setFilter('unread')}
                    >
                      {t('Non lus')}
                      {unread > 0 && <span>{unread}</span>}
                    </button>
                    <IconButton
                      label={t('Afficher les favoris')}
                      active={filter === 'starred'}
                      onClick={() => setFilter(filter === 'starred' ? 'all' : 'starred')}
                    >
                      <Star size={17} />
                    </IconButton>
                  </div>
                  <div className="list-toolbar">
                    <label className="select-all">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) =>
                          setSelectedIds(
                            e.target.checked ? new Set(messages.map((m) => m.id)) : new Set(),
                          )
                        }
                        aria-label={t('Sélectionner tous les messages affichés')}
                      />
                    </label>
                    <ChevronDown size={13} />
                    {selectedIds.size ? (
                      <>
                        <span className="selected-label">
                          {t(selectedIds.size > 1 ? '{n} sélectionnés' : '{n} sélectionné', {
                            n: selectedIds.size,
                          })}
                        </span>
                        <IconButton
                          label={t(
                            currentFolder?.kind === 'trash'
                              ? 'Restaurer la sélection dans la réception'
                              : 'Archiver la sélection',
                          )}
                          disabled={busy}
                          onClick={() =>
                            void action(currentFolder?.kind === 'trash' ? 'restore' : 'archive', [
                              ...selectedIds,
                            ])
                          }
                        >
                          {currentFolder?.kind === 'trash' ? (
                            <Undo2 size={16} />
                          ) : (
                            <Archive size={16} />
                          )}
                        </IconButton>
                        <IconButton
                          label={t(
                            currentFolder?.kind === 'trash'
                              ? 'Supprimer définitivement la sélection'
                              : 'Supprimer la sélection',
                          )}
                          disabled={busy}
                          onClick={() =>
                            void action(currentFolder?.kind === 'trash' ? 'delete' : 'trash', [
                              ...selectedIds,
                            ])
                          }
                        >
                          <Trash2 size={16} />
                        </IconButton>
                        <IconButton
                          label={t('Marquer la sélection comme lue')}
                          disabled={busy}
                          onClick={() => void action('read', [...selectedIds])}
                        >
                          <MailOpen size={16} />
                        </IconButton>
                      </>
                    ) : (
                      <span>
                        {next
                          ? t('{n} messages affichés', { n: messages.length })
                          : t(messages.length > 1 ? '{n} messages' : '{n} message', {
                              n: messages.length,
                            })}
                      </span>
                    )}
                    <span className="spacer" />
                    <span className="sort-label">
                      {t('Plus récents')}
                      <ChevronDown size={12} />
                    </span>
                  </div>
                  <StorageNotice
                    used={bootstrap.mailbox.usedBytes}
                    quota={bootstrap.mailbox.quotaBytes}
                    from={90}
                  />
                  <div className="message-list">
                    {loading && !messages.length ? (
                      <Loading />
                    ) : dateRangeError ? (
                      <div className="empty-state">
                        <h3>{t('Période incorrecte')}</h3>
                        <p>{dateRangeError}</p>
                      </div>
                    ) : !messages.length ? (
                      <div className="empty-state">
                        <span className="empty-icon">
                          <Inbox size={32} />
                        </span>
                        <h3>{debounced ? t('Aucun résultat') : t('Tout est à sa place.')}</h3>
                        <p>
                          {debounced
                            ? t('Essayez un autre mot ou ajustez vos filtres.')
                            : t('Ce dossier ne contient aucun message pour le moment.')}
                        </p>
                      </div>
                    ) : (
                      messages.map((m) => (
                        <div
                          key={m.id}
                          className={`message-row ${!m.read ? 'unread' : ''} ${selected?.id === m.id ? 'selected' : ''} ${selectedIds.has(m.id) ? 'checked' : ''}`}
                        >
                          <div className="row-checkbox">
                            <input
                              type="checkbox"
                              aria-label={t('Sélectionner {subject}', { subject: m.subject })}
                              checked={selectedIds.has(m.id)}
                              onChange={(e) => {
                                const ids = new Set(selectedIds);
                                e.target.checked ? ids.add(m.id) : ids.delete(m.id);
                                setSelectedIds(ids);
                              }}
                            />
                          </div>
                          <button
                            className="message-open"
                            onClick={() => void openMessage(m)}
                            aria-label={`Ouvrir ${m.subject}, de ${m.fromName || m.from}${m.read ? '' : ', non lu'}`}
                          >
                            <Avatar name={m.fromName || m.from} />
                            <div className="message-row-content">
                              <div className="message-row-top">
                                <strong>{m.fromName || m.from}</strong>
                                <time>{displayDate(m.date)}</time>
                              </div>
                              <div className="message-subject">
                                <span>{m.subject || t('(sans objet)')}</span>
                                {!m.read && <span className="unread-dot" />}
                              </div>
                              <p>{m.preview}</p>
                              {m.attachments.length > 0 && (
                                <span className="attachment-chip">
                                  <Paperclip size={12} />
                                  {m.attachments[0].name}
                                  {m.attachments.length > 1 && ` +${m.attachments.length - 1}`}
                                </span>
                              )}
                            </div>
                          </button>
                          <button
                            className={`star-button ${m.starred ? 'starred' : ''}`}
                            aria-label={
                              m.starred
                                ? `Retirer des favoris : ${m.subject}`
                                : `Ajouter aux favoris : ${m.subject}`
                            }
                            onClick={() => void action(m.starred ? 'unstar' : 'star', [m.id])}
                          >
                            <Star size={16} fill={m.starred ? 'currentColor' : 'none'} />
                          </button>
                        </div>
                      ))
                    )}
                    {next && (
                      <button
                        className="load-more"
                        onClick={() => void loadMessages(true)}
                        disabled={loading}
                      >
                        {loading ? t('Chargement…') : t('Afficher les messages suivants')}
                        <ChevronDown size={15} />
                      </button>
                    )}
                  </div>
                  <div className="list-footer">
                    <ShieldCheck size={13} />
                    {t('Vos échanges, dans votre espace.')}
                  </div>
                </section>
                <section className="reader-pane" aria-label={t('Lecture du message')}>
                  {reading && !selected ? (
                    <Loading label={t('Ouverture du message…')} />
                  ) : selected ? (
                    <>
                      <div className="reader-toolbar">
                        <IconButton
                          className="reader-back"
                          label={t('Revenir à la liste')}
                          onClick={() => setSelected(undefined)}
                        >
                          <ArrowLeft size={19} />
                        </IconButton>
                        {['trash', 'spam'].includes(
                          bootstrap.folders.find((f) => f.id === selected.folderId)?.kind ?? '',
                        ) ? (
                          <IconButton
                            label={t('Restaurer dans la réception')}
                            onClick={() => void action('restore', [selected.id])}
                            disabled={busy}
                          >
                            <Inbox size={19} />
                          </IconButton>
                        ) : (
                          <>
                            <IconButton
                              label={t('Archiver le message')}
                              onClick={() => void action('archive', [selected.id])}
                              disabled={busy}
                            >
                              <Archive size={19} />
                            </IconButton>
                            <IconButton
                              label={t('Signaler comme indésirable')}
                              onClick={() => void action('spam', [selected.id])}
                              disabled={busy}
                            >
                              <ShieldAlert size={19} />
                            </IconButton>
                            <IconButton
                              label={t('Mettre à la corbeille')}
                              onClick={() => void action('trash', [selected.id])}
                              disabled={busy}
                            >
                              <Trash2 size={19} />
                            </IconButton>
                          </>
                        )}
                        <span className="toolbar-separator" />
                        <IconButton
                          label={t('Marquer comme non lu')}
                          onClick={() => void action('unread', [selected.id])}
                        >
                          <Mail size={19} />
                        </IconButton>
                        <IconButton
                          label={t('Déplacer vers un dossier')}
                          onClick={() => setModal('move')}
                        >
                          <FolderInput size={19} />
                        </IconButton>
                        <span className="spacer" />
                        <span className="reader-pagination">
                          {t('{index} sur {total}', {
                            index: selectedIndex + 1,
                            total: messages.length,
                          })}
                        </span>
                        <IconButton
                          label={t('Message précédent')}
                          disabled={selectedIndex <= 0}
                          onClick={() => void openMessage(messages[selectedIndex - 1])}
                        >
                          <ChevronLeft size={18} />
                        </IconButton>
                        <IconButton
                          label={t('Message suivant')}
                          disabled={selectedIndex < 0 || selectedIndex >= messages.length - 1}
                          onClick={() => void openMessage(messages[selectedIndex + 1])}
                        >
                          <ChevronRight size={18} />
                        </IconButton>
                      </div>
                      <div className="message-detail">
                        <div className="detail-title">
                          <h2>{selected.subject || t('(sans objet)')}</h2>
                          <IconButton
                            label={
                              selected.starred ? t('Retirer le favori') : t('Ajouter aux favoris')
                            }
                            onClick={() =>
                              void action(selected.starred ? 'unstar' : 'star', [selected.id])
                            }
                          >
                            <Star
                              size={20}
                              className={selected.starred ? 'starred' : ''}
                              fill={selected.starred ? 'currentColor' : 'none'}
                            />
                          </IconButton>
                        </div>
                        <span className="reader-folder-badge">
                          {bootstrap.folders.find((f) => f.id === selected.folderId)?.name}
                        </span>
                        <div className="sender-line">
                          <Avatar name={selected.fromName || selected.from} />
                          <div className="sender-info">
                            <div>
                              <strong>{selected.fromName || selected.from}</strong>
                              <span>&lt;{selected.from}&gt;</span>
                            </div>
                            <details>
                              <summary>
                                {t('À')}{' '}
                                {selected.to
                                  .map((a) => (bootstrap.fromAddresses.includes(a) ? t('moi') : a))
                                  .join(', ')}
                                <ChevronDown size={12} />
                              </summary>
                              <div className="recipient-details">
                                <p>{t('De : {v}', { v: selected.from })}</p>
                                <p>{t('À : {v}', { v: selected.to.join(', ') })}</p>
                                {selected.cc.length > 0 && (
                                  <p>{t('Cc : {v}', { v: selected.cc.join(', ') })}</p>
                                )}
                                {selected.bcc.length > 0 && (
                                  <p>{t('Cci : {v}', { v: selected.bcc.join(', ') })}</p>
                                )}
                                <p>Date : {displayDate(selected.date, true)}</p>
                              </div>
                            </details>
                          </div>
                          <div className="sender-date">
                            <time>{displayDate(selected.date)}</time>
                            <div>
                              <IconButton
                                label={t('Répondre')}
                                onClick={() => startCompose({ mode: 'reply', message: selected })}
                              >
                                <Reply size={18} />
                              </IconButton>
                              <div className="dropdown-container">
                                <IconButton
                                  label={t('Plus d’actions sur le message')}
                                  onClick={() => setMessageMenu(!messageMenu)}
                                >
                                  <MoreHorizontal size={19} />
                                </IconButton>
                                {messageMenu && (
                                  <div className="dropdown-menu">
                                    <a href={mailboxUrl(`/v1/messages/${selected.id}/raw`)}>
                                      <Download size={16} />
                                      {t('Télécharger l’original (.eml)')}
                                    </a>
                                    <button
                                      onClick={() => {
                                        setModal('headers');
                                        setMessageMenu(false);
                                      }}
                                    >
                                      <FileText size={16} />
                                      {t('Afficher les en-têtes')}
                                    </button>
                                    <button
                                      onClick={() => {
                                        setTextOnly(!textOnly);
                                        setMessageMenu(false);
                                      }}
                                    >
                                      <Mail size={16} />
                                      {textOnly
                                        ? t('Afficher le contenu enrichi')
                                        : t('Afficher la version texte')}
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        {bootstrap.mailbox?.kind === 'shared' && (
                          <ConversationWork
                            key={selected.id}
                            messageId={selected.id}
                            onOpenMessage={(m) => void openMessage(m)}
                          />
                        )}
                        {!textOnly && selected.html && (
                          <div className="privacy-note">
                            <ShieldCheck size={14} />
                            {t(
                              'Images distantes et contenus actifs bloqués. Les images intégrées autorisées sont affichées.',
                            )}
                          </div>
                        )}
                        {!textOnly && selected.html ? (
                          <iframe
                            className="safe-mail"
                            title={t('Contenu enrichi du message')}
                            sandbox="allow-popups allow-popups-to-escape-sandbox"
                            referrerPolicy="no-referrer"
                            srcDoc={`<!doctype html><meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data:; base-uri 'none'; form-action 'none'"><style>body{font:14px/1.8 system-ui;color:#363747;margin:0;overflow-wrap:anywhere}img{max-width:100%;height:auto}table{max-width:100%}a{color:#5546d7}blockquote{border-left:3px solid #ddd;padding-left:12px}</style>${selected.html}`}
                          />
                        ) : (
                          <div className="message-body">{selected.text}</div>
                        )}
                        {selected.attachments.length > 0 && (
                          <div className="reader-attachments">
                            <h3>
                              <Paperclip size={16} />
                              {t(
                                selected.attachments.length > 1
                                  ? '{n} pièces jointes'
                                  : '{n} pièce jointe',
                                { n: selected.attachments.length },
                              )}
                            </h3>
                            {selected.attachments.map((a) => (
                              <a
                                className="attachment-card"
                                key={a.id}
                                href={mailboxUrl(`/v1/messages/${selected.id}/attachments/${a.id}`)}
                              >
                                <span className="file-symbol">
                                  <FileText size={25} />
                                </span>
                                <span>
                                  <strong>{a.name}</strong>
                                  <small>
                                    {formatBytes(a.size)}
                                    {' · '}
                                    {bootstrap.fromAddresses.some(
                                      (address) =>
                                        address.toLowerCase() === selected.from.toLowerCase(),
                                    )
                                      ? t('Pièce jointe envoyée')
                                      : t('Pièce jointe reçue')}
                                    {' · '}
                                    {a.scanStatus === 'local_unscanned'
                                      ? t('Statut d’analyse antivirus indisponible')
                                      : a.scanStatus === 'clean'
                                        ? t('Analyse antivirus terminée')
                                        : a.scanStatus === 'blocked'
                                          ? t('Pièce jointe bloquée')
                                          : t('Analyse antivirus en attente')}
                                  </small>
                                </span>
                                <Download size={18} />
                              </a>
                            ))}
                          </div>
                        )}
                        {['spam', 'trash'].includes(
                          bootstrap.folders.find((f) => f.id === selected.folderId)?.kind ?? '',
                        ) ? (
                          <div className="reply-actions">
                            <button
                              className="secondary"
                              onClick={() => void action('restore', [selected.id])}
                            >
                              <Inbox size={17} />
                              {t('Restaurer dans la réception')}
                            </button>
                            {currentFolder?.kind === 'trash' && (
                              <button
                                className="secondary danger"
                                onClick={() => void action('delete', [selected.id])}
                              >
                                <Trash2 size={17} />
                                {t('Supprimer définitivement')}
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="reply-actions">
                            <button
                              className="secondary"
                              onClick={() => startCompose({ mode: 'reply', message: selected })}
                            >
                              <Reply size={18} />
                              {t('Répondre')}
                            </button>
                            {selected.to.length + selected.cc.length > 1 && (
                              <button
                                className="secondary"
                                onClick={() =>
                                  startCompose({ mode: 'replyAll', message: selected })
                                }
                              >
                                <ReplyAll size={18} />
                                {t('À tous')}
                              </button>
                            )}
                            <button
                              className="secondary"
                              onClick={() => startCompose({ mode: 'forward', message: selected })}
                            >
                              <Forward size={18} />
                              {t('Transférer')}
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="reader-empty">
                      <div className="empty-art">
                        <div className="art-ring" />
                        <span className="art-envelope">
                          <Mail size={47} strokeWidth={1.2} />
                        </span>
                        <span className="art-check">
                          <Check size={16} />
                        </span>
                        <span className="art-spark">✦</span>
                      </div>
                      <span className="eyebrow">{t('PLACE À L’ESSENTIEL')}</span>
                      <h2>{t('Bonjour {name}.', { name: bootstrap.user.name.split(' ')[0] })}</h2>
                      <p>
                        {unread
                          ? t(
                              unread > 1
                                ? 'Vous avez {n} messages à découvrir.'
                                : 'Vous avez {n} message à découvrir.',
                              { n: unread },
                            )
                          : t('Tout est à jour dans votre réception.')}
                        <br />
                        {t('Sélectionnez un message pour commencer.')}
                      </p>
                      <div className="empty-shortcut">
                        <span>{t('Un nouveau message ?')}</span>
                        <button onClick={() => startCompose()}>
                          {t('C’est par ici')}
                          <ChevronRight size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </section>
              </div>
            </main>
          )}
          {agendaOpen && !['calendar', 'contacts', 'team'].includes(view) && (
            <AgendaPanel
              onClose={() => toggleAgenda(false)}
              onOpenCalendar={(date) => openCalendarAt(date)}
              onMoreOptions={(date) => openCalendarAt(date, true)}
              notify={notify}
            />
          )}
        </div>
        <footer className="app-footer">
          <span>Vename Mail</span>
          <span>
            <ShieldCheck size={12} />
            {t('Un espace pour vos échanges professionnels.')}
          </span>
        </footer>
      </div>
      {toast && (
        <div className="toast" role="status">
          <span>
            <Check size={16} />
          </span>
          {toast}
          <IconButton label={t('Fermer la notification')} onClick={() => setToast('')}>
            <X size={16} />
          </IconButton>
        </div>
      )}
      {compose && (
        <Composer
          key={`${compose.mode}-${compose.message?.id ?? 'new'}`}
          bootstrap={bootstrap}
          seed={compose}
          onClose={() => setCompose(undefined)}
          onSaved={() => {
            void loadBootstrap();
            if (folder === 'drafts') void loadMessages();
          }}
          onSent={(id, scheduledAt) => {
            if (scheduledAt) {
              notify(t('Envoi programmé le {date}.', { date: displayDate(scheduledAt, true) }));
              void loadBootstrap();
              if (folder === 'drafts') void loadMessages();
              return;
            }
            setSubmission({
              id,
              status: 'pending',
              recipients: [],
              createdAt: new Date().toISOString(),
            });
            notify(t('Votre envoi est pris en charge.'));
            void refresh();
          }}
        />
      )}
      {modal === 'help' && (
        <Modal title={t('Un coup de main ?')} onClose={() => setModal('')}>
          <div className="stack-form">
            <p>{t('Vename Mail vous aide à recevoir, rédiger et retrouver vos messages.')}</p>
            <div className="help-item">
              <Search size={21} />
              <div>
                <h3>{t('Retrouver un échange')}</h3>
                <p>
                  {t(
                    t(
                      'Recherchez un mot de l’objet, le texte du message ou un expéditeur. Le raccourci Ctrl / ⌘ K ouvre la recherche.',
                    ),
                  )}
                </p>
              </div>
            </div>
            <div className="help-item">
              <FilePenLine size={21} />
              <div>
                <h3>{t('Écrire à votre rythme')}</h3>
                <p>
                  {t(
                    t(
                      'Le brouillon est enregistré après une courte pause. En cas de coupure, gardez l’onglet ouvert ou copiez votre texte.',
                    ),
                  )}
                </p>
              </div>
            </div>
            <div className="help-item">
              <ShieldCheck size={21} />
              <div>
                <h3>{t('Préserver votre confidentialité')}</h3>
                <p>
                  {t(
                    t(
                      'Les images distantes sont bloquées. Vous pouvez télécharger vos messages au format .eml depuis le menu de lecture.',
                    ),
                  )}
                </p>
              </div>
            </div>
            <div className="help-item">
              <Mail size={21} />
              <div>
                <h3>{t('Besoin d’une aide humaine ?')}</h3>
                <p>
                  {t(
                    'Écrivez au support Vename, même depuis une adresse extérieure à votre organisation.',
                  )}
                </p>
                <a href="mailto:support@vename.com">{t('Contacter le support Vename')}</a>
              </div>
            </div>
            {bootstrap.mode === 'local' && (
              <Notice>
                {t(
                  t(
                    'Pour tester les échanges, utilisez alice@kora.test, malik@kora.test ou awa@naya.test. Aucun email ne quitte l’environnement local.',
                  ),
                )}
              </Notice>
            )}
          </div>
        </Modal>
      )}
      {modal === 'notifications' && (
        <Modal title={t('Vos notifications')} onClose={() => setModal('')}>
          <div className="stack-form">
            {submission ? (
              <>
                <h3>{t('Dernier envoi')}</h3>
                <Notice
                  type={
                    submission.status === 'failed'
                      ? 'error'
                      : submission.status === 'delivered_local'
                        ? 'success'
                        : 'info'
                  }
                >
                  {submission.status === 'delivered_local'
                    ? t('Message remis dans les boîtes locales.')
                    : submission.status === 'pending'
                      ? t('Votre message attend sa prise en charge.')
                      : submission.status === 'unknown'
                        ? t(
                            t(
                              'Le résultat est incertain. Ne renvoyez pas le message avant vérification.',
                            ),
                          )
                        : submission.status === 'deferred'
                          ? t('Une remise est différée.')
                          : t('État : {status}', { status: submission.status })}
                </Notice>
                {submission.recipients.map((r) => (
                  <div key={r.address} className="preferences-row">
                    <strong>{r.address}</strong>
                    <span>
                      {r.status === 'delivered_local'
                        ? t('Remis localement')
                        : (r.detail ?? r.status)}
                    </span>
                  </div>
                ))}
              </>
            ) : (
              <div className="empty-state">
                <Bell size={30} />
                <h3>{t('Tout est à jour.')}</h3>
                <p>{t('Les informations concernant vos envois apparaîtront ici.')}</p>
              </div>
            )}
            <StorageNotice
              used={bootstrap.mailbox.usedBytes}
              quota={bootstrap.mailbox.quotaBytes}
              from={80}
            />
          </div>
        </Modal>
      )}
      {(modal === 'folder' || modal === 'folderEdit') && (
        <Modal
          title={modal === 'folder' ? t('Créer un dossier') : t('Gérer le dossier')}
          onClose={() => setModal('')}
        >
          <form
            className="stack-form"
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                await send(
                  modal === 'folder' ? '/folders' : `/folders/${folderEdit!.id}`,
                  { name: newFolder },
                  modal === 'folder' ? 'POST' : 'PATCH',
                );
                await loadBootstrap();
                setModal('');
                notify(t('Dossier enregistré.'));
              } catch (e: any) {
                setError(e.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            <label>
              {t('Nom du dossier')}
              <input
                value={newFolder}
                onChange={(e) => setNewFolder(e.target.value)}
                required
                maxLength={80}
                autoFocus
              />
            </label>
            {error && <Notice type="error">{error}</Notice>}
            <div className="modal-actions">
              {modal === 'folderEdit' && (
                <button
                  type="button"
                  className="text-button danger"
                  onClick={async () => {
                    if (
                      !window.confirm(
                        t(
                          'Supprimer le dossier « {name} » ? Ses {n} message(s) seront déplacés vers la corbeille.',
                          { name: folderEdit!.name, n: folderEdit!.total },
                        ),
                      )
                    )
                      return;
                    try {
                      await send(
                        `/folders/${folderEdit!.id}`,
                        { delete: true, confirmCount: folderEdit!.total },
                        'PATCH',
                      );
                      await loadBootstrap();
                      navigate('inbox');
                      setModal('');
                      notify(t('Dossier supprimé.'));
                    } catch (e: any) {
                      setError(e.message);
                    }
                  }}
                >
                  {t('Supprimer le dossier')}
                </button>
              )}
              <button className="primary" disabled={busy}>
                {t('Enregistrer')}
              </button>
            </div>
          </form>
        </Modal>
      )}
      {modal === 'move' && selected && (
        <Modal title={t('Déplacer le message')} onClose={() => setModal('')}>
          <div className="move-folders">
            {bootstrap.folders
              .filter((f) => f.kind !== 'drafts' && f.id !== selected.folderId)
              .map((f) => (
                <button
                  key={f.id}
                  onClick={() => {
                    void action('move', [selected.id], f.id);
                    setModal('');
                  }}
                >
                  <Folder size={18} />
                  {f.name}
                  <ChevronRight size={15} />
                </button>
              ))}
          </div>
        </Modal>
      )}
      {modal === 'headers' && selected && (
        <Modal title={t('En-têtes du message')} onClose={() => setModal('')} wide>
          <pre className="headers-content">{selected.headers}</pre>
        </Modal>
      )}
      {modal === 'folderInfo' && (
        <Modal title={title} onClose={() => setModal('')}>
          <div className="stack-form">
            <p>{currentFolder?.total ?? messages.length} message(s) dans ce dossier.</p>
            <p>
              {t(
                t(
                  'Les listes sont chargées par pages de 50 messages. La recherche reste limitée à votre boîte.',
                ),
              )}
            </p>
            {['trash', 'spam'].includes(folder) && (
              <Notice>
                {t(
                  t(
                    'Les messages de ce dossier sont purgés après 30 jours. Restaurez ceux que vous souhaitez conserver.',
                  ),
                )}
              </Notice>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
function CloudIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <path d="M6 18a4.5 4.5 0 0 1-.5-9 6.5 6.5 0 0 1 12.6 0A4.5 4.5 0 0 1 18 18Z" />
    </svg>
  );
}
function ArrowUpRightIcon() {
  return <ExternalLink size={14} />;
}

const mfaReminderKey = 'vename.mfa-reminder';
/** Reminder for an account without a second factor; « Plus tard » hides it for 30 days. */
function MfaReminder({ onOpen }: { onOpen: () => void }) {
  const [hidden, setHidden] = useState(() => {
    try {
      return Number(localStorage.getItem(mfaReminderKey)) > Date.now();
    } catch {
      return false;
    }
  });
  if (hidden) return null;
  return (
    <div className="autoreply-bar mfa-reminder" role="status">
      <ShieldAlert size={16} />
      <span>{t('Protégez votre compte avec la double authentification.')}</span>
      <button className="text-button" onClick={onOpen}>
        {t('Activer')}
      </button>
      <button
        className="text-button"
        onClick={() => {
          try {
            localStorage.setItem(mfaReminderKey, String(Date.now() + 30 * 86_400_000));
          } catch {
            // Without storage the reminder simply comes back at the next visit.
          }
          setHidden(true);
        }}
      >
        {t('Plus tard')}
      </button>
    </div>
  );
}

/**
 * Mailbox storage alert: 80 % warns, 90 % asks for action, 100 % means new messages are refused.
 * `from` hides the lower levels where a quieter place already shows them.
 */
function StorageNotice({ used, quota, from }: { used: number; quota: number; from: 80 | 90 }) {
  const level = storageLevel(used, quota);
  if (level < from) return null;
  return (
    <div className="storage-notice">
      <Notice type={level === 100 ? 'error' : 'info'}>
        {level === 100
          ? t(
              'Votre boîte est pleine : les nouveaux messages sont refusés temporairement jusqu’à ce que vous libériez de l’espace. Videz la corbeille, archivez ou demandez à votre administrateur d’augmenter votre plafond.',
            )
          : level === 90
            ? t(
                'Votre boîte utilise plus de 90 % de son quota. Videz la corbeille ou supprimez des pièces jointes volumineuses : à 100 %, les nouveaux messages seront refusés.',
              )
            : t(
                'Votre boîte utilise plus de 80 % de son quota. Libérez de l’espace avant d’atteindre la limite.',
              )}
      </Notice>
    </div>
  );
}
